import { useState, useEffect, lazy, Suspense } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Home,
  Bed,
  Bath,
  Car,
  Ruler,
  MapPin,
  MessageCircle,
  Mail,
  Check,
  X,
  Share2,
  ChevronLeft,
  ChevronRight,
  Clock
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { propertyService, leadService } from "../services/api";
import { toast } from "sonner";

// Tracking pixels helper
const TrackingPixels = ({ property }) => {
  useEffect(() => {
    if (!property) return;

    // Facebook Pixel with Advanced Matching
    if (property.facebook_pixel_id) {
      const fbScript = document.createElement('script');
      fbScript.innerHTML = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${property.facebook_pixel_id}');
        fbq('track', 'PageView');
      `;
      document.head.appendChild(fbScript);
    }

    // Google Analytics 4
    if (property.google_analytics_id) {
      const gaScript = document.createElement('script');
      gaScript.async = true;
      gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${property.google_analytics_id}`;
      document.head.appendChild(gaScript);

      const gaConfig = document.createElement('script');
      gaConfig.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${property.google_analytics_id}');
      `;
      document.head.appendChild(gaConfig);
    }

    // Google Ads
    if (property.google_ads_id) {
      const gadsScript = document.createElement('script');
      gadsScript.async = true;
      gadsScript.src = `https://www.googletagmanager.com/gtag/js?id=${property.google_ads_id}`;
      document.head.appendChild(gadsScript);

      const gadsConfig = document.createElement('script');
      gadsConfig.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${property.google_ads_id}');
      `;
      document.head.appendChild(gadsConfig);
    }

    // TikTok Pixel
    if (property.tiktok_pixel_id) {
      const ttScript = document.createElement('script');
      ttScript.innerHTML = `
        !function (w, d, t) {
          w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
          ttq.load('${property.tiktok_pixel_id}');
          ttq.page();
        }(window, document, 'ttq');
      `;
      document.head.appendChild(ttScript);
    }

    // Cleanup function
    return () => {
      // Scripts will persist during session, which is expected behavior
    };
  }, [property]);

  return null;
};

// Track conversion events with Advanced Matching data
const trackConversion = (property, eventType, userData) => {
  // Build standard custom_data with value + currency (required by Meta to avoid "missing currency/value" warnings)
  const customData = {
    content_name: property.title,
    content_type: 'product',
    content_ids: [property.id || property.slug],
    value: Number(property.price) || 0,
    currency: 'MXN'
  };

  // Facebook with Advanced Matching
  if (property?.facebook_pixel_id && window.fbq) {
    const eventId = `${eventType}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const options = { eventID: eventId };
    
    // Send user data for Advanced Matching if available
    if (userData) {
      const nameParts = (userData.name || '').trim().split(' ');
      const matchData = {};
      if (userData.email) matchData.em = userData.email;
      if (userData.phone) matchData.ph = userData.phone.replace(/[^0-9]/g, '');
      if (nameParts[0]) matchData.fn = nameParts[0].toLowerCase();
      if (nameParts.length > 1) matchData.ln = nameParts.slice(1).join(' ').toLowerCase();
      if (Object.keys(matchData).length > 0) {
        window.fbq('init', property.facebook_pixel_id, matchData);
      }
    }
    
    if (eventType === 'lead') {
      window.fbq('track', 'Lead', customData, options);
    } else if (eventType === 'contact') {
      window.fbq('track', 'Contact', customData, options);
    }
  }

  // Google Analytics
  if (property?.google_analytics_id && window.gtag) {
    window.gtag('event', eventType === 'lead' ? 'generate_lead' : 'contact', {
      event_category: 'engagement',
      event_label: property.title,
      value: Number(property.price) || 0,
      currency: 'MXN'
    });
  }

  // Google Ads
  if (property?.google_ads_id && window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: property.google_ads_id,
      value: Number(property.price) || 0,
      currency: 'MXN'
    });
  }

  // TikTok
  if (property?.tiktok_pixel_id && window.ttq) {
    const tiktokData = {
      content_name: property.title,
      content_id: property.id || property.slug,
      value: Number(property.price) || 0,
      currency: 'MXN'
    };
    if (eventType === 'lead') {
      window.ttq.track('SubmitForm', tiktokData);
    } else if (eventType === 'contact') {
      window.ttq.track('Contact', tiktokData);
    }
  }
};

// Helper function to build image URLs with proper encoding
const getImageUrl = (imagePath, { width = 800, quality = 'auto:low' } = {}) => {
  if (!imagePath) return null;
  
  // If it's a Cloudinary URL, insert optimization transformations
  if (imagePath.includes('res.cloudinary.com') && imagePath.includes('/upload/')) {
    // Insert f_webp,q_auto:low,w_XXX after /upload/
    const transform = `f_webp,q_${quality},w_${width},c_limit`;
    return imagePath.replace('/upload/', `/upload/${transform}/`);
  }
  
  // If it's another full URL, return as-is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  
  // Handle both old paths (/uploads/...) and new paths (/api/uploads/...)
  let normalizedPath = imagePath;
  if (imagePath.startsWith('/uploads/')) {
    normalizedPath = '/api' + imagePath;
  }
  
  // Split path into segments and encode each segment individually
  const segments = normalizedPath.split('/');
  const encodedSegments = segments.map((segment) => {
    // Don't encode empty segments or known path parts
    if (segment === '' || segment === 'api' || segment === 'uploads' || segment === 'properties') {
      return segment;
    }
    // Encode special characters in file/folder names
    return encodeURIComponent(segment);
  });
  return `${baseUrl}${encodedSegments.join('/')}`;
};

export const PropertyLanding = () => {
  const { slug } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [isDraftPreview, setIsDraftPreview] = useState(false);

  useEffect(() => {
    loadProperty();
  }, [slug]);

  const loadProperty = async () => {
    try {
      const data = await propertyService.getBySlug(slug);
      setProperty(data);
      // Check if this is a draft preview (owner viewing their unpublished property)
      if (data.status === "draft" || !data.is_published) {
        setIsDraftPreview(true);
      }
      setError(null);
    } catch (err) {
      console.error("Error loading property:", err);
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      
      if (status === 403) {
        setError({
          type: "unavailable",
          message: detail || "Esta propiedad no está disponible temporalmente."
        });
      } else if (status === 404) {
        setError({
          type: "not_found",
          message: "Propiedad no encontrada"
        });
      } else {
        setError({
          type: "error",
          message: "Error al cargar la propiedad"
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await leadService.create({
        property_id: property.id,
        ...formData
      });
      // Track lead conversion event with user data for Advanced Matching
      trackConversion(property, 'lead', formData);
      toast.success("¡Mensaje enviado! Te contactaremos pronto");
      setFormData({ name: "", phone: "", email: "", message: "" });
    } catch (error) {
      toast.error("Error al enviar mensaje");
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
      minimumFractionDigits: 0
    }).format(price);
  };

  const openWhatsApp = () => {
    // Track contact conversion event (pass form data if user filled any)
    trackConversion(property, 'contact', formData.name || formData.email ? formData : null);
    const customMsg = property.whatsapp_message?.trim();
    const baseMsg = customMsg
      ? customMsg.replace(/\{title\}/gi, property.title).replace(/\{titulo\}/gi, property.title)
      : `Hola, estoy interesado en la propiedad: ${property.title}`;
    const message = encodeURIComponent(baseMsg);
    // Normalize WhatsApp number: strip non-digits, auto-prefix MX country code if 10 digits
    const raw = String(property.whatsapp_number || "").replace(/\D+/g, "");
    let phone = raw;
    if (raw.length === 10) {
      // 10-digit MX number without country code → prefix +52
      phone = `52${raw}`;
    } else if (raw.length === 11 && raw.startsWith("1")) {
      // 11-digit US/Canada number with leading 1 → keep as-is
      phone = raw;
    }
    window.open(`https://wa.me/${phone}?text=${message}`, "_blank");
  };

  const handleShare = async () => {
    const baseUrl = window.location.origin;
    // Use OG-tagged share URL so WhatsApp/FB show the property title and a clean preview
    const shareUrl = `${baseUrl}/api/share/${property.slug}`;
    // Build a short teaser from description (max ~140 chars on word boundary)
    const rawDesc = (property.description || "").replace(/\n+/g, " ").trim();
    const teaser = rawDesc.length > 140 ? rawDesc.slice(0, 140).replace(/\s+\S*$/, "") + "..." : rawDesc;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: property.title,
          text: teaser,
          url: shareUrl
        });
      } catch (error) {
        copyToClipboard(shareUrl);
      }
    } else {
      copyToClipboard(shareUrl);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("¡Link copiado!");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Cargando propiedad...</p>
      </div>
    );
  }

  // Show error page when property is unavailable
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            {error.type === "unavailable" ? (
              <Clock className="w-10 h-10 text-amber-500" />
            ) : (
              <Home className="w-10 h-10 text-gray-400" />
            )}
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            {error.type === "unavailable" ? "Propiedad No Disponible" : "Propiedad No Encontrada"}
          </h1>
          
          <p className="text-gray-600 mb-6">
            {error.message}
          </p>
          
          {error.type === "unavailable" && (
            <p className="text-sm text-gray-500 mb-6">
              Esta propiedad estará disponible nuevamente cuando el agente reactive su suscripción.
            </p>
          )}
          
          <div className="space-y-3">
            <Link to="/">
              <Button className="w-full bg-brand-purple hover:bg-brand-pink text-white">
                <Home className="w-4 h-4 mr-2" />
                Ir al Inicio
              </Button>
            </Link>
            <Link to="/precios">
              <Button variant="outline" className="w-full">
                ¿Eres agente? Ver planes
              </Button>
            </Link>
          </div>
          
          {/* Branding */}
          <div className="mt-8 pt-6 border-t">
            <a href="https://inmopages.app" className="inline-flex items-center gap-2 text-gray-400 hover:text-gray-600 text-sm">
              <span>Powered by</span>
              <img 
                src="https://customer-assets.emergentagent.com/job_proplandpro/artifacts/d99lgtpt_inmopages-fondoblanco.png" 
                alt="InmoPages" 
                className="h-5 opacity-60 hover:opacity-100 transition-opacity"
              />
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Propiedad no encontrada</p>
          <Link to="/">
            <Button className="bg-brand-purple hover:bg-brand-pink text-white">
              Volver al Inicio
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Custom colors from property
  const primaryColor = property.primary_color || "#a033fd";
  const secondaryColor = property.secondary_color || "#ff1758";

  // Custom styles object for dynamic colors
  const customStyles = {
    primaryBg: { backgroundColor: primaryColor },
    secondaryBg: { backgroundColor: secondaryColor },
    primaryText: { color: primaryColor },
    primaryBorder: { borderColor: primaryColor },
    gradientBg: { background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` },
  };

  // Template props to pass to alternative templates
  const templateProps = {
    property,
    formData,
    setFormData,
    handleSubmit,
    submitting,
    openWhatsApp,
    handleShare,
    isDraftPreview,
    getImageUrl
  };

  // Render alternative templates
  if (property.template === "moderna") {
    const ModernaTemplate = require("./templates/ModernaTemplate").default;
    return (
      <>
        <TrackingPixels property={property} />
        <ModernaTemplate {...templateProps} />
      </>
    );
  }
  
  if (property.template === "elegante") {
    const EleganteTemplate = require("./templates/EleganteTemplate").default;
    return (
      <>
        <TrackingPixels property={property} />
        <EleganteTemplate {...templateProps} />
      </>
    );
  }

  // Default: "clasica" template
  return (
    <div className="min-h-screen bg-gray-50" style={{ '--primary-color': primaryColor, '--secondary-color': secondaryColor }}>
      {/* Marketing Tracking Pixels */}
      <TrackingPixels property={property} />
      
      {/* Draft Preview Banner - only visible to owner */}
      {isDraftPreview && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-amber-500 text-white text-center py-2 px-4 text-sm font-medium shadow-lg">
          Vista previa — Esta propiedad no es visible al público.{" "}
          <a href="/precios" className="underline font-bold">Activa tu plan para publicarla</a>
        </div>
      )}
      
      {/* Floating Action Buttons - Top Right */}
      <div className="fixed top-4 right-4 z-50 flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleShare}
          className="bg-white/90 backdrop-blur-sm shadow-lg border-gray-200 hover:bg-white"
        >
          <Share2 className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">Compartir</span>
        </Button>
        {(property.contact_method !== "form") && (
          <Button
            size="sm"
            onClick={openWhatsApp}
            className="bg-green-600 hover:bg-green-700 text-white shadow-lg"
          >
            <MessageCircle className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">WhatsApp</span>
          </Button>
        )}
      </div>

      {/* Hero Section - Full Width Image with Overlay */}
      <section className="relative h-[70vh] min-h-[500px]">
        {/* Background Image */}
        {property.main_image ? (
          <img
            src={getImageUrl(property.main_image, { width: 1200, quality: 'auto:low' })}
            alt={property.title}
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="property-main-image"
          />
        ) : property.images && property.images.length > 0 ? (
          <img
            src={getImageUrl(property.images[0], { width: 1200, quality: 'auto:low' })}
            alt={property.title}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-brand-purple to-brand-pink" />
        )}
        
        {/* Dark Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
        
        {/* Hero Content */}
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-8">
          {/* Property Type & Status Badge */}
          <div className="mb-4 flex flex-wrap gap-2">
            <span 
              className="inline-block text-white text-sm font-semibold px-4 py-1.5 rounded-full uppercase tracking-wide"
              style={customStyles.primaryBg}
            >
              {property.property_type} en {property.operation_type || 'Venta'}
            </span>
            {property.status && property.status !== 'active' && (
              <span className={`inline-block text-white text-sm font-semibold px-4 py-1.5 rounded-full uppercase tracking-wide ${
                property.status === 'sold' ? 'bg-red-600' : 
                property.status === 'rented' ? 'bg-orange-600' : 
                property.status === 'reserved' ? 'bg-yellow-600' : 'bg-gray-600'
              }`}>
                {property.status === 'sold' ? 'Vendida' : 
                 property.status === 'rented' ? 'Rentada' : 
                 property.status === 'reserved' ? 'Reservada' : property.status}
              </span>
            )}
          </div>
          
          {/* Title */}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 max-w-3xl">
            {property.title}
          </h1>
          
          {/* Location */}
          <div className="flex items-center text-white/90 mb-6">
            <MapPin className="w-5 h-5 mr-2" />
            <span className="text-lg">{property.location}</span>
          </div>
          
          {/* Price & Quick Specs Row */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            {/* Price + Gallery Button (Mobile) */}
            <div className="flex items-end justify-between sm:justify-start gap-4">
              <div>
                <p className="text-white/70 text-sm mb-1">Precio</p>
                <p className="text-3xl sm:text-5xl font-bold text-white">
                  {formatPrice(property.price)}
                </p>
              </div>
              
              {/* Gallery Button - Mobile Only */}
              {property.images && property.images.length > 0 && (
                <button
                  onClick={() => setSelectedImage(0)}
                  className="sm:hidden bg-white/90 hover:bg-white text-gray-900 px-3 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors shadow-lg text-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {property.images.length} fotos
                </button>
              )}
            </div>
            
            {/* Quick Specs - Hidden on Mobile */}
            <div className="hidden sm:flex flex-wrap gap-3">
              {property.specs?.bedrooms && (
                <div className="flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <Bed className="w-5 h-5 text-white mr-2" />
                  <span className="text-white font-semibold">{property.specs.bedrooms}</span>
                  <span className="text-white/80 ml-1">Rec.</span>
                </div>
              )}
              {property.specs?.bathrooms && (
                <div className="flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <Bath className="w-5 h-5 text-white mr-2" />
                  <span className="text-white font-semibold">{property.specs.bathrooms}</span>
                  <span className="text-white/80 ml-1">Baños</span>
                </div>
              )}
              {property.specs?.square_meters && (
                <div className="flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <Ruler className="w-5 h-5 text-white mr-2" />
                  <span className="text-white font-semibold">{property.specs.square_meters}</span>
                  <span className="text-white/80 ml-1">m²</span>
                </div>
              )}
              {property.specs?.parking && (
                <div className="flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <Car className="w-5 h-5 text-white mr-2" />
                  <span className="text-white font-semibold">{property.specs.parking}</span>
                  <span className="text-white/80 ml-1">Est.</span>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Gallery Button - Desktop Only */}
        {property.images && property.images.length > 0 && (
          <button
            onClick={() => setSelectedImage(0)}
            className="hidden sm:flex absolute bottom-8 right-8 bg-white/90 hover:bg-white text-gray-900 px-4 py-2 rounded-lg font-medium items-center gap-2 transition-colors shadow-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Ver {property.images.length} fotos
          </button>
        )}
      </section>

      {/* CTA Bar - Mobile Sticky */}
      <div className="lg:hidden sticky top-0 z-40 bg-white border-b border-gray-200 p-4">
        <div className="flex gap-3">
          {(property.contact_method !== "form") && (
            <Button
              onClick={openWhatsApp}
              className={`${property.contact_method === "whatsapp" ? "flex-1" : "flex-1"} bg-green-600 hover:bg-green-700 text-white`}
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp
            </Button>
          )}
          {(property.contact_method !== "whatsapp") && (
            <Button
              onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
              className="flex-1 text-white"
              style={customStyles.primaryBg}
            >
              <Mail className="w-4 h-4 mr-2" />
              Contactar
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Details */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* All Specs Card */}
            {(property.specs?.bedrooms || property.specs?.bathrooms || property.specs?.parking || 
              property.specs?.square_meters || property.specs?.construction_meters) && (
              <Card>
                <CardContent className="pt-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Especificaciones</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {property.specs?.bedrooms && (
                      <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                        <Bed className="w-6 h-6" style={customStyles.primaryText} />
                        <div>
                          <p className="font-semibold text-gray-900">{property.specs.bedrooms}</p>
                          <p className="text-sm text-gray-500">Recámaras</p>
                        </div>
                      </div>
                    )}
                    {property.specs?.bathrooms && (
                      <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                        <Bath className="w-6 h-6" style={customStyles.primaryText} />
                        <div>
                          <p className="font-semibold text-gray-900">{property.specs.bathrooms}</p>
                          <p className="text-sm text-gray-500">Baños</p>
                        </div>
                      </div>
                    )}
                    {property.specs?.half_bathrooms && (
                      <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                        <Bath className="w-6 h-6" style={customStyles.primaryText} />
                        <div>
                          <p className="font-semibold text-gray-900">{property.specs.half_bathrooms}</p>
                          <p className="text-sm text-gray-500">Medios Baños</p>
                        </div>
                      </div>
                    )}
                    {property.specs?.parking && (
                      <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                        <Car className="w-6 h-6" style={customStyles.primaryText} />
                        <div>
                          <p className="font-semibold text-gray-900">{property.specs.parking}</p>
                          <p className="text-sm text-gray-500">Estacionamientos</p>
                        </div>
                      </div>
                    )}
                    {property.specs?.square_meters && (
                      <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                        <Ruler className="w-6 h-6" style={customStyles.primaryText} />
                        <div>
                          <p className="font-semibold text-gray-900">{property.specs.square_meters} m²</p>
                          <p className="text-sm text-gray-500">Terreno</p>
                        </div>
                      </div>
                    )}
                    {property.specs?.construction_meters && (
                      <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                        <Ruler className="w-6 h-6" style={customStyles.primaryText} />
                        <div>
                          <p className="font-semibold text-gray-900">{property.specs.construction_meters} m²</p>
                          <p className="text-sm text-gray-500">Construcción</p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Image Gallery */}
            {property.images && property.images.length > 0 && (
              <Card>
                <CardContent className="pt-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Galería de Fotos</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {property.images.map((image, index) => (
                      <div
                        key={index}
                        className="aspect-square rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity border-2 border-gray-200 hover:border-brand-purple"
                        onClick={() => setSelectedImage(index)}
                        data-testid={`gallery-image-${index}`}
                      >
                        <img
                          src={getImageUrl(image, { width: 400, quality: 'auto:low' })}
                          alt={`Imagen ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Image Modal */}
            {selectedImage !== null && property.images && property.images[selectedImage] && (
              <div
                className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
                onClick={() => setSelectedImage(null)}
                data-testid="image-modal"
              >
                {/* Close button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedImage(null);
                  }}
                  className="absolute top-4 right-4 text-white bg-red-600 hover:bg-red-700 p-3 rounded-full z-10 transition-colors"
                  data-testid="close-modal-btn"
                >
                  <X className="w-6 h-6" />
                </button>

                {/* Left Arrow */}
                {selectedImage > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImage(selectedImage - 1);
                    }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white p-4 rounded-full transition-all z-10 backdrop-blur-sm"
                    data-testid="prev-image-btn"
                  >
                    <ChevronLeft className="w-8 h-8" />
                  </button>
                )}

                {/* Right Arrow */}
                {selectedImage < property.images.length - 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImage(selectedImage + 1);
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white p-4 rounded-full transition-all z-10 backdrop-blur-sm"
                    data-testid="next-image-btn"
                  >
                    <ChevronRight className="w-8 h-8" />
                  </button>
                )}
                
                {/* Image Container */}
                <div className="relative max-w-6xl w-full px-16">
                  <img
                    src={getImageUrl(property.images[selectedImage], { width: 1400, quality: 'auto:good' })}
                    alt={`Imagen ${selectedImage + 1}`}
                    className="w-full max-h-[85vh] object-contain rounded-lg"
                    onClick={(e) => e.stopPropagation()}
                  />
                  
                  {/* Counter */}
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-black/70 text-white px-4 py-2 rounded-full font-semibold text-sm backdrop-blur-sm">
                      {selectedImage + 1} / {property.images.length}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Description */}
            <Card>
              <CardContent className="pt-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Descripción</h2>
                <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                  {property.description}
                </p>
              </CardContent>
            </Card>

            {/* Amenities */}
            {((property.amenities && property.amenities.length > 0) || 
              property.specs?.tv_room || property.specs?.laundry_room || 
              property.specs?.service_room || property.specs?.roof_garden || 
              property.specs?.balcony || property.specs?.terrace) && (
              <Card>
                <CardContent className="pt-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Características</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {property.amenities?.map((amenity, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <Check className="w-5 h-5 text-green-600" />
                        <span>{amenity.name}</span>
                      </div>
                    ))}
                    {property.specs?.tv_room && (
                      <div className="flex items-center space-x-2">
                        <Check className="w-5 h-5 text-green-600" />
                        <span>Estar de TV</span>
                      </div>
                    )}
                    {property.specs?.laundry_room && (
                      <div className="flex items-center space-x-2">
                        <Check className="w-5 h-5 text-green-600" />
                        <span>Cuarto de Lavado</span>
                      </div>
                    )}
                    {property.specs?.service_room && (
                      <div className="flex items-center space-x-2">
                        <Check className="w-5 h-5 text-green-600" />
                        <span>Cuarto de Servicio</span>
                      </div>
                    )}
                    {property.specs?.roof_garden && (
                      <div className="flex items-center space-x-2">
                        <Check className="w-5 h-5 text-green-600" />
                        <span>Roof Garden</span>
                      </div>
                    )}
                    {property.specs?.balcony && (
                      <div className="flex items-center space-x-2">
                        <Check className="w-5 h-5 text-green-600" />
                        <span>Balcón</span>
                      </div>
                    )}
                    {property.specs?.terrace && (
                      <div className="flex items-center space-x-2">
                        <Check className="w-5 h-5 text-green-600" />
                        <span>Terraza</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Video */}
            {property.video_embed_url && (
              <Card>
                <CardContent className="pt-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Video</h2>
                  <div className="aspect-video">
                    <iframe
                      src={property.video_embed_url}
                      className="w-full h-full rounded-lg"
                      allowFullScreen
                      title="Video de la propiedad"
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Map */}
            {property.latitude && property.longitude && 
             property.latitude >= -90 && property.latitude <= 90 &&
             property.longitude >= -180 && property.longitude <= 180 ? (
              <Card>
                <CardContent className="pt-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Ubicación</h2>
                  
                  <div className="aspect-video rounded-lg overflow-hidden border-2 border-gray-200 bg-gray-100">
                    <iframe
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      scrolling="no"
                      marginHeight="0"
                      marginWidth="0"
                      src={`https://www.openstreetmap.org/export/embed.html?bbox=${property.longitude - 0.01},${property.latitude - 0.01},${property.longitude + 0.01},${property.latitude + 0.01}&layer=mapnik&marker=${property.latitude},${property.longitude}`}
                      title="Mapa de ubicación"
                    />
                  </div>
                  
                  <div className="mt-4 space-y-2">
                    {property.address?.street && (
                      <p className="text-gray-600 flex items-start">
                        <MapPin className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                        <span>
                          {property.address.street}
                          {property.address.exterior_number && ` ${property.address.exterior_number}`}
                          {property.address.interior_number && ` Int. ${property.address.interior_number}`}
                          {property.address.neighborhood && `, ${property.address.neighborhood}`}
                          {property.address.city && `, ${property.address.city}`}
                          {property.address.state && `, ${property.address.state}`}
                          {property.address.postal_code && ` C.P. ${property.address.postal_code}`}
                        </span>
                      </p>
                    )}
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${property.latitude},${property.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-brand-purple hover:text-brand-pink transition-colors font-medium"
                    >
                      Abrir en Google Maps →
                    </a>
                  </div>
                </CardContent>
              </Card>
            ) : property.location ? (
              <Card>
                <CardContent className="pt-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Ubicación</h2>
                  <div className="p-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-center text-gray-600 font-medium">{property.location}</p>
                    <p className="text-center text-sm text-gray-500 mt-2">
                      Agrega coordenadas (latitud/longitud) para mostrar el mapa interactivo
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : null}
          </div>

          {/* Right Column - Contact */}
          <div className="lg:col-span-1">
            {/* WhatsApp-only mode */}
            {property.contact_method === "whatsapp" && (
              <Card 
                id="contact-form"
                className="sticky top-20 shadow-lg border-2"
                style={{ borderColor: `${primaryColor}30` }}
              >
                <CardContent className="pt-6 text-center">
                  <span 
                    className="inline-block text-sm font-semibold px-3 py-1 rounded-full mb-3"
                    style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
                  >
                    Contacto directo
                  </span>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {property.form_title || "¿Te Interesa?"}
                  </h2>
                  <p className="text-gray-600 mb-6">
                    {property.form_description || "Escríbenos por WhatsApp y te atendemos de inmediato"}
                  </p>
                  <Button
                    type="button"
                    onClick={openWhatsApp}
                    className="w-full bg-green-600 hover:bg-green-700 text-white h-14 text-lg font-semibold"
                    data-testid="whatsapp-only-btn"
                  >
                    <MessageCircle className="w-6 h-6 mr-2" />
                    Contactar por WhatsApp
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Form mode or both mode */}
            {property.contact_method !== "whatsapp" && (
            <Card 
              id="contact-form" 
              className="sticky top-20 shadow-lg border-2"
              style={{ borderColor: `${primaryColor}30` }}
            >
              <CardContent className="pt-6">
                <div className="text-center mb-6">
                  <span 
                    className="inline-block text-sm font-semibold px-3 py-1 rounded-full mb-3"
                    style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
                  >
                    Agenda tu visita
                  </span>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {property.form_title || "¿Te Interesa?"}
                  </h2>
                  <p className="text-gray-600 mt-2">
                    {property.form_description || "Te contactamos en menos de 24 horas"}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Nombre *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Tu nombre"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Teléfono *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+52 333 123 4567"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="tu@email.com"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">Mensaje (opcional)</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="¿Tienes alguna pregunta?"
                      rows={3}
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full text-white h-12 text-base font-semibold"
                    style={customStyles.primaryBg}
                    disabled={submitting}
                  >
                    {submitting ? "Enviando..." : "Solicitar Información"}
                  </Button>

                  {(property.contact_method === "both") && (
                  <>
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">o contacta directo</span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    onClick={openWhatsApp}
                    className="w-full bg-green-600 hover:bg-green-700 text-white h-12 text-base font-semibold"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    WhatsApp Directo
                  </Button>
                  </>
                  )}
                </form>
              </CardContent>
            </Card>
            )}
          </div>
        </div>
      </div>

      {/* Discreet Footer - Powered by InmoPages */}
      <footer className="bg-gray-100 border-t border-gray-200 py-6 mt-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <a 
            href="https://inmopages.app" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-gray-600 transition-colors text-sm"
          >
            <span>Desarrollado en</span>
            <img 
              src="https://customer-assets.emergentagent.com/job_proplandpro/artifacts/d99lgtpt_inmopages-fondoblanco.png" 
              alt="InmoPages" 
              className="h-5 opacity-60 hover:opacity-100 transition-opacity"
            />
          </a>
        </div>
      </footer>
    </div>
  );
};
